import { Alert, type FormInstance } from 'antd';
import isEqual from 'lodash/isEqual';
import React, { memo, useMemo } from 'react';
import { useDeepCompareEffect } from 'react-use';

import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import BackOfficeDeviceFormItems from 'components/forms/BackOfficeDeviceFormItems';
import { BackOfficeDevice } from 'store/modules/backOfficeDevice/types';
import { useErrorMessage } from 'store/modules/loading/hooks';
import { FILTER_NULL } from 'store/modules/routerUtils/selectors';
import { getDeviceLocation } from 'utils/get-location';

interface Props {
  /** e.g., 'EDIT_BACK_OFFICE_DEVICE_REQUEST' */
  actions: string[];
  form: FormInstance;
  initialValues?: BackOfficeDevice;
  readOnly?: boolean;
}

function BackOfficeDeviceForm({
  actions,
  form,
  initialValues,
  readOnly
}: Props): JSX.Element | null {
  const errorMessage = useErrorMessage(actions);

  useDeepCompareEffect(() => {
    form.resetFields();
  }, [form, initialValues]);

  const parsedInitialValues = useMemo(() => {
    if (!initialValues) {
      return initialValues;
    }
    const parsed: Record<string, unknown> = {};
    // We want to be able to explicitly set enterpriseId & siteId to be null in
    // the form, but the form dropdown cannot use 'null' as an options' value.
    // Instead, we map to a constant FILTER_NULL when filling the form, and map
    // back to 'null' on form submission.
    Object.entries(initialValues).forEach(([key, value]) => {
      if (['enterpriseId', 'siteId'].includes(key)) {
        parsed[key] = value ? Number(value) : FILTER_NULL;
      } else {
        parsed[key] = value;
      }
    });

    return parsed;
  }, [initialValues]);

  const location = useMemo(
    () => initialValues ? getDeviceLocation(initialValues) : undefined,
    [initialValues]
  );

  return (
    <DefaultForm
      form={form}
      layout="vertical"
      name="backOfficeDevice"
      autoComplete="chrome-off"
      initialValues={parsedInitialValues}
      requiredMark={!readOnly && 'optional'}
      scrollToFirstError
      closeDrawerIfFieldsAreValid={false}
    >
      <BackOfficeDeviceFormItems
        initialEnterpriseId={initialValues?.enterpriseId}
        initialSiteId={initialValues?.siteId}
        location={location}
        readOnly={readOnly}
      />
      {errorMessage && (
        <DefaultFormItem>
          <Alert message={errorMessage} type="error" />
        </DefaultFormItem>
      )}
    </DefaultForm>
  );
}

export default memo(BackOfficeDeviceForm, isEqual);
